/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ActionKernelType,
  ActionType,
  DetailType,
  UnifiedCollectionDetailType,
  VodOttActionType
} from "../../model/types";
import type {
  Action,
  ContentScreenDetails,
  TPreview,
  TUnifiedCollectionScreenDetails
} from "../../model/types";
import { Locale, LocaleKeys } from "../../model/locale";
import {
  formatSeasonEpisode,
  getActionIconKind,
  getContentTypeLabel,
  getScheduleActionLabelKey,
  getScheduleActionTypeFallbackKey,
  getSectionLabel,
  getVideoResolutionLabel,
  getWatchActionLabelKey,
  getWatchActionTypeFallbackKey
} from "./contentDetailStrings";
import type {
  ContentDetailActionData,
  ContentDetailActionSubtitleData,
  ContentDetailBadgeData,
  ContentDetailKind,
  ContentDetailRecordingParams,
  ContentDetailSectionData,
  ContentDetailSectionId,
  ContentDetailState,
  ContentDetailStreamingServiceData
} from "./contentDetail.types";

/**
 * Pure mapping functions from `@tivocorp/data-domain-lib` DTOs (re-exported
 * via `../../model/types/TContentDetailDto` — the viewModel layer must not
 * import the package directly, see ai_docs/REDUX_GOVERNANCE.md section 6.1)
 * to `ContentDetailState`. No side effects, no logging — safe to unit test
 * with plain object fixtures.
 *
 * The action-catalog mapping (kernel → row → label) implements
 * docs/unity-client-doc/doc/screens/content/actions/00-actions.md,
 * 01-actions.content.md, and 01-actions.series.md — those three files are
 * the source of truth for exact `ActionKernel Type` strings and Watch vs
 * Schedule row grouping. The kernel → locale key tables themselves live in
 * contentDetailStrings.ts, the web analogue of Android's
 * `DetailsScreenUiStrings`.
 */

/**
 * Recognized-but-unrendered kernels — the "Not Planned (Legacy)" tables in
 * 01-actions.content.md / 01-actions.series.md, plus the entire Rent/
 * Subscribe "TODO" catalog. Filtered out silently; this is documented
 * behavior, not an error condition.
 *
 * A kernel absent from both label tables in contentDetailStrings.ts is
 * excluded the same way, so this set is documentation as much as behavior —
 * it names the kernels the catalog knows about and deliberately drops.
 */
const NOT_PLANNED_KERNELS: ReadonlySet<string> = new Set<string>([
  ActionKernelType.InfoOfferActionKernel,
  ActionKernelType.InfoContentActionKernel,
  ActionKernelType.InfoRecordingActionKernel,
  ActionKernelType.MoreInfoContentActionKernel,
  ActionKernelType.MoreInfoUnifiedCollectionActionKernel,
  ActionKernelType.RentThisShowActionKernel,
  ActionKernelType.RentAndWatchNowActionKernel,
  ActionKernelType.RentCallAheadActionKernel,
  ActionKernelType.RentAndWatchCallAheadActionKernel,
  ActionKernelType.RentOptionsUnavailableActionKernel,
  ActionKernelType.SubscribeToSocuActionKernel,
  ActionKernelType.SubscribeToNdvrActionKernel
]);

/** Section stacking order. Matches Figma: episodes, upcoming, cast, posters. */
const SECTION_ORDER: ContentDetailSectionId[] = [
  "episodes",
  "upcoming",
  "cast",
  "mayAlsoLike"
];

const SECONDS_PER_MINUTE: number = 60;
const MINUTES_PER_HOUR: number = 60;

/**
 * The object ids and section flags a `detailList.details[]` scan yields.
 * Android derives all four in one pass (`DetailsScreenDataMapper`); web used
 * to derive only the sections and throw the ids away, leaving the sections
 * with nothing to fetch with.
 */
interface DetailScanResult {
  sectionIds: Set<ContentDetailSectionId>;
  episodeGuideObjectId: string | null;
  upcomingObjectId: string | null;
  mayAlsoLikeObjectId: string | null;
}

/** A `detailList.details[]` entry, read structurally past its base type. */
interface RawDetail {
  detailType: string;
  actions?: Action[];
  collectionId?: string;
  contentId?: string;
  unifiedCollectionId?: string;
}

/**
 * The eight kernels that schedule a *single* recording, as opposed to a
 * OnePass. Copied from Android's `extractSingleRecordingParams` — the
 * generated kernels carry the same four fields but share no parameter-
 * bearing base type, so membership is a list, not a type test.
 */
const SINGLE_RECORDING_KERNELS: ReadonlySet<string> = new Set<string>([
  "recordAgainActionKernel",
  "recordThisShowActionKernel",
  "recordThisMovieActionKernel",
  "recordThisEpisodeActionKernel",
  "recordThisGameActionKernel",
  "recordThisMatchActionKernel",
  "recordThisRaceActionKernel",
  "recordThisEventActionKernel"
]);

/**
 * Lifts the recording parameters off a single-recording action kernel.
 * Returns `undefined` for every other kernel type.
 */
function extractRecordingParams(
  action: Action
): ContentDetailRecordingParams | undefined {
  if (!SINGLE_RECORDING_KERNELS.has(action.kernel.actionKernelType)) {
    return undefined;
  }
  const kernel = action.kernel as unknown as Record<string, unknown>;
  const readString = (key: string): string | undefined =>
    typeof kernel[key] === "string" && (kernel[key] as string).length > 0
      ? (kernel[key] as string)
      : undefined;

  return {
    offerId: readString("offerId"),
    contentId: readString("contentId"),
    channelNumber: readString("channelNumber"),
    channelId: readString("channelId"),
    airingStartTime: readString("airingStartTime"),
    duration: readActionNumber(action.kernel, "duration")
  };
}

// ─── Action classification ───

/** A mapped pill plus the keys its de-duplication and ordering key off. */
interface KeyedAction {
  labelKey: string;
  dedupeKey: string;
  data: ContentDetailActionData;
}

/** Which row an action lands in, and the locale key of its label. */
interface ActionPlacement {
  row: "watch" | "schedule";
  labelKey: string;
}

/** Reads a string field off an action or kernel past its generated base type. */
function readActionString(source: object, key: string): string | undefined {
  const value: unknown = (source as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readActionNumber(source: object, key: string): number | undefined {
  const value: unknown = (source as Record<string, unknown>)[key];
  return typeof value === "number" ? value : undefined;
}

/** `WatchFromProviderAction.provider.displayName`, when present. */
function readProviderDisplayName(action: Action): string | undefined {
  const provider: unknown = (action as unknown as Record<string, unknown>)
    .provider;
  return typeof provider === "object" && provider !== null
    ? readActionString(provider, "displayName")
    : undefined;
}

/** "S5 E14" for a `watchUnifiedCollectionAction` that names its episode. */
function resolveLabelSuffix(action: Action): string | undefined {
  if (action.actionType !== ActionType.WatchUnifiedCollectionAction) {
    return undefined;
  }
  return formatSeasonEpisode(
    readActionNumber(action, "seasonNumber"),
    readActionNumber(action, "episodeNumber")
  );
}

/** First `typeId + valueId` pair of an action's `internalRatingList`. */
function readFirstRatingKey(action: Action): string | undefined {
  const list: unknown = (action as unknown as Record<string, unknown>)
    .internalRatingList;
  if (!Array.isArray(list)) {
    return undefined;
  }
  for (const rating of list as Array<{ typeId?: unknown; valueId?: unknown }>) {
    if (
      (typeof rating.typeId === "string" ||
        typeof rating.typeId === "number") &&
      (typeof rating.valueId === "string" || typeof rating.valueId === "number")
    ) {
      return String(rating.typeId) + String(rating.valueId);
    }
  }
  return undefined;
}

/**
 * Subtitle under a focused Watch pill — Android `liveTvSubtitle` and
 * `vodWatchActionSubtitles`. `undefined` for every other action.
 */
function resolveWatchSubtitle(
  action: Action
): ContentDetailActionSubtitleData | undefined {
  if (
    action.kernel.actionKernelType ===
    ActionKernelType.StreamingOptionsActionKernel
  ) {
    return {
      kind: "verbatim",
      text: Locale.getString(
        LocaleKeys.CONTENT_ACTION_LABEL_STREAMING_OPTIONS_SUBTITLE
      )
    };
  }
  if (action.actionType === ActionType.LiveTvAction) {
    const dateTimeRange: string | undefined = formatAiringRange(
      readActionString(action, "startTime"),
      readActionString(action, "endTime")
    );
    if (dateTimeRange === undefined) {
      return undefined;
    }
    return {
      kind: "offer",
      dateTimeRange,
      ratingKey: readFirstRatingKey(action),
      resolution: getVideoResolutionLabel(
        readActionString(action, "videoResolution")
      ),
      callSign: readActionString(action, "channelCallSign")
    };
  }
  const providerName: string | undefined = readProviderDisplayName(action);
  if (
    action.kernel.actionKernelType ===
      ActionKernelType.WatchFromVodActionKernel &&
    providerName !== undefined
  ) {
    return { kind: "provider", providerName };
  }
  return undefined;
}

/**
 * The provider tile behind a `watchFromOttActionKernel` action, or `undefined`
 * when the kernel carries no partner id to build a logo from.
 */
function toOttProvider(
  action: Action
): ContentDetailStreamingServiceData | undefined {
  const partnerId: string | undefined = readActionString(
    action.kernel,
    "partnerId"
  );
  if (partnerId === undefined) {
    return undefined;
  }
  const kernelName: string | undefined = readActionString(
    action.kernel,
    "providerDisplayName"
  );
  const name: string | undefined =
    kernelName !== undefined ? kernelName : readProviderDisplayName(action);
  return {
    id: partnerId,
    name: name !== undefined ? name : "",
    actionId: action.kernel.actionId
  };
}

/** Adds the OTT action's provider tile unless one with its id is already listed. */
function addOttProvider(
  providers: ContentDetailStreamingServiceData[],
  action: Action
): void {
  const provider: ContentDetailStreamingServiceData | undefined =
    toOttProvider(action);
  if (
    provider !== undefined &&
    !providers.some(
      (existing: ContentDetailStreamingServiceData): boolean =>
        existing.id === provider.id
    )
  ) {
    providers.push(provider);
  }
}

/** Appends `action` unless a pill with the same visible label is already listed. */
function addUnique(actions: KeyedAction[], action: KeyedAction): void {
  if (
    !actions.some(
      (existing: KeyedAction): boolean =>
        existing.dedupeKey === action.dedupeKey
    )
  ) {
    actions.push(action);
  }
}

/**
 * Row and label key: the kernel tables first, then the `actionType`
 * fallbacks. `undefined` drops the action.
 */
function resolveActionPlacement(
  kernelType: string,
  actionType: string
): ActionPlacement | undefined {
  const watchKey: string | undefined = getWatchActionLabelKey(kernelType);
  if (watchKey !== undefined) {
    return { row: "watch", labelKey: watchKey };
  }
  const scheduleKey: string | undefined = getScheduleActionLabelKey(kernelType);
  if (scheduleKey !== undefined) {
    return { row: "schedule", labelKey: scheduleKey };
  }
  const watchFallback: string | undefined =
    getWatchActionTypeFallbackKey(actionType);
  if (watchFallback !== undefined) {
    return { row: "watch", labelKey: watchFallback };
  }
  const scheduleFallback: string | undefined =
    getScheduleActionTypeFallbackKey(actionType);
  return scheduleFallback !== undefined
    ? { row: "schedule", labelKey: scheduleFallback }
    : undefined;
}

function toWatchAction(action: Action, labelKey: string): KeyedAction {
  const suffix: string | undefined = resolveLabelSuffix(action);
  const baseLabel: string = Locale.getString(labelKey);
  return {
    labelKey,
    dedupeKey: suffix !== undefined ? labelKey + "|" + suffix : labelKey,
    data: {
      id: action.kernel.actionId,
      label: suffix !== undefined ? baseLabel + " " + suffix : baseLabel,
      variant: "primary",
      isDisabled: false,
      kernelType: action.kernel.actionKernelType,
      sourceAction: action,
      iconKind: getActionIconKind(
        action.kernel.actionKernelType,
        action.actionType
      ),
      subtitle: resolveWatchSubtitle(action)
    }
  };
}

function toScheduleAction(action: Action, labelKey: string): KeyedAction {
  return {
    labelKey,
    dedupeKey: labelKey,
    data: {
      id: action.kernel.actionId,
      label: Locale.getString(labelKey),
      variant: "secondary",
      isDisabled: false,
      kernelType: action.kernel.actionKernelType,
      sourceAction: action,
      iconKind: getActionIconKind(
        action.kernel.actionKernelType,
        action.actionType
      ),
      recordingParams: extractRecordingParams(action)
    }
  };
}

/** Android appends Streaming Options after every other Watch pill. */
function moveStreamingOptionsLast(actions: KeyedAction[]): KeyedAction[] {
  const isStreamingOptions = (action: KeyedAction): boolean =>
    action.data.kernelType === ActionKernelType.StreamingOptionsActionKernel;
  return actions
    .filter((action: KeyedAction): boolean => !isStreamingOptions(action))
    .concat(actions.filter(isStreamingOptions));
}

/**
 * Android `orderScheduleActions`: a single-recording "Record" ahead of
 * "Record Series", and "Manage Recordings" last.
 */
function orderScheduleActions(actions: KeyedAction[]): KeyedAction[] {
  const ordered: KeyedAction[] = actions.slice();
  const indexOfKey = (key: string): number =>
    ordered.findIndex(
      (action: KeyedAction): boolean => action.labelKey === key
    );

  const recordIndex: number = indexOfKey(
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD
  );
  const seriesIndex: number = indexOfKey(
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD_SERIES
  );
  if (recordIndex !== -1 && seriesIndex !== -1 && recordIndex > seriesIndex) {
    ordered.splice(seriesIndex, 0, ordered.splice(recordIndex, 1)[0]);
  }

  const manageIndex: number = indexOfKey(
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS
  );
  if (manageIndex !== -1 && manageIndex !== ordered.length - 1) {
    ordered.push(ordered.splice(manageIndex, 1)[0]);
  }
  return ordered;
}

/**
 * Splits a raw `Action[]` list into Watch Actions row / Schedule Actions row
 * data, de-duplicated by resulting button label (first occurrence wins),
 * with "Not Planned" kernels silently excluded. A kernel in neither label
 * table falls back to its `actionType` label (Android
 * `ACTION_TYPE_FALLBACK_LABELS`) and is dropped only when that is unknown too.
 *
 * `watchFromOttActionKernel` actions never become pills: like Android's
 * `WatchActionsSection`, they are returned as `ottProviders` and rendered as
 * provider logo tiles after the pills.
 */
function mapActionsToRows(actions: Action[]): {
  watchActions: ContentDetailActionData[];
  scheduleActions: ContentDetailActionData[];
  ottProviders: ContentDetailStreamingServiceData[];
} {
  const watchActions: KeyedAction[] = [];
  const scheduleActions: KeyedAction[] = [];
  const ottProviders: ContentDetailStreamingServiceData[] = [];

  for (const action of actions) {
    const kernelType: string = action.kernel.actionKernelType;
    if (NOT_PLANNED_KERNELS.has(kernelType)) {
      continue;
    }
    if (kernelType === ActionKernelType.WatchFromOttActionKernel) {
      addOttProvider(ottProviders, action);
      continue;
    }
    const placement: ActionPlacement | undefined = resolveActionPlacement(
      kernelType,
      action.actionType
    );
    if (placement === undefined) {
      continue;
    }
    // Out of scrop for 5.0
    if (
      placement.labelKey === LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE ||
      placement.labelKey === LocaleKeys.CONTENT_ACTION_LABEL_REMOVE_FAVORITE
    ) {
      continue;
    }
    if (placement.row === "watch") {
      addUnique(watchActions, toWatchAction(action, placement.labelKey));
    } else {
      addUnique(scheduleActions, toScheduleAction(action, placement.labelKey));
    }
  }

  return {
    watchActions: moveStreamingOptionsLast(watchActions).map(
      (action: KeyedAction): ContentDetailActionData => action.data
    ),
    scheduleActions: orderScheduleActions(scheduleActions).map(
      (action: KeyedAction): ContentDetailActionData => action.data
    ),
    ottProviders
  };
}

// ─── Section + object-id derivation ───

export function applyContentDetailStreamingOptions(
  data: Partial<ContentDetailState>,
  details: import("@tivocorp/data-domain-lib").VodOttActionDetailsList
): void {
  const choices: ContentDetailActionData[] = [];
  for (const watch of details.watchActionsList === undefined
    ? []
    : details.watchActionsList) {
    const raw: Record<string, unknown> = { ...watch.kernel };
    const offerId = readActionString(watch.kernel, "offerId");
    if (offerId === undefined) {
      continue;
    }
    const isSocu = watch.actionType === VodOttActionType.WatchFromSoCuAction;
    const isVod = watch.actionType === VodOttActionType.WatchFromVodAction;
    if (!isSocu && !isVod) {
      continue;
    }
    const channel: unknown = raw.channel;
    const channelNumber =
      channel !== null && typeof channel === "object"
        ? readActionString(channel, "channelNumber")
        : undefined;
    const kernelType = isSocu
      ? ActionKernelType.WatchFromSocuActionKernel
      : ActionKernelType.WatchFromVodOfferActionKernel;
    const sourceAction: Action = {
      actionType: ActionType.WatchFromProviderAction,
      kernel: {
        ...watch.kernel,
        actionId: offerId,
        actionKernelType: kernelType,
        ...{ offerId, socuOfferId: offerId, channelNumber }
      }
    };
    const label = Locale.getString(
      isSocu
        ? LocaleKeys.CONTENT_ACTION_LABEL_CATCHUP
        : LocaleKeys.CONTENT_ACTION_LABEL_ON_DEMAND
    );
    const vodName = readActionString(watch.kernel, "vodName");
    const provider =
      vodName !== undefined
        ? vodName
        : readActionString(watch.kernel, "partnerId");
    choices.push({
      id: offerId,
      label,
      variant: "primary",
      isDisabled: raw.isStreamingRestrictedByDeviceType === true,
      kernelType,
      sourceAction,
      iconKind: "play",
      subtitle:
        provider === undefined
          ? undefined
          : { kind: "provider", providerName: provider }
    });
  }
  const socu = choices.filter(
    (action) => action.kernelType === ActionKernelType.WatchFromSocuActionKernel
  );
  const vod = choices.filter(
    (action) =>
      action.kernelType === ActionKernelType.WatchFromVodOfferActionKernel
  );
  const selected = socu.length > 0 ? socu : vod;
  data.watchActions = (
    data.watchActions === undefined ? [] : data.watchActions
  ).flatMap((action): ContentDetailActionData[] => {
    if (action.kernelType !== ActionKernelType.StreamingOptionsActionKernel) {
      return [action];
    }
    if (selected.length === 0) {
      return [];
    }
    if (selected.length === 1) {
      return selected;
    }
    return [
      {
        ...action,
        label: Locale.getString(
          socu.length > 0
            ? LocaleKeys.CONTENT_ACTION_LABEL_CATCHUP_OPTIONS
            : LocaleKeys.CONTENT_ACTION_LABEL_ON_DEMAND_OPTIONS
        ),
        streamingOptions: selected
      }
    ];
  });
}

function buildSections(
  presentSectionIds: ReadonlySet<ContentDetailSectionId>
): ContentDetailSectionData[] {
  const sections: ContentDetailSectionData[] = [];
  for (const id of SECTION_ORDER) {
    if (presentSectionIds.has(id)) {
      sections.push({ id, label: getSectionLabel(id) });
    }
  }
  return sections;
}

/** Returns `value` when it is a non-empty string, otherwise `undefined`. */
function nonEmpty(value: string | undefined): string | undefined {
  return value !== undefined && value.length > 0 ? value : undefined;
}

/**
 * Keeps the first non-null candidate — the web equivalent of Android's
 * `firstNotNullOfOrNull` over `detailList.details[]`.
 */
function firstDefined(
  current: string | null,
  candidate: string | undefined
): string | null {
  if (current !== null) {
    return current;
  }
  const resolved: string | undefined = nonEmpty(candidate);
  return resolved !== undefined ? resolved : null;
}

/**
 * Single pass over a `ContentScreenDetails` detail list, deriving both the
 * visible sections and the object id each section fetches with — see
 * `DetailsScreenDataMapper.toContent*ObjectId`.
 */
function scanContentDetails(details: RawDetail[]): DetailScanResult {
  const result: DetailScanResult = {
    sectionIds: new Set<ContentDetailSectionId>(),
    episodeGuideObjectId: null,
    upcomingObjectId: null,
    mayAlsoLikeObjectId: null
  };

  for (const detail of details) {
    switch (detail.detailType) {
      case DetailType.SeasonOrYearsDetail:
      case DetailType.OnDemandEpisodesDetail:
        result.sectionIds.add("episodes");
        result.episodeGuideObjectId = firstDefined(
          result.episodeGuideObjectId,
          detail.collectionId
        );
        break;
      case DetailType.UpcomingAiringDetail:
        result.sectionIds.add("upcoming");
        result.upcomingObjectId = firstDefined(
          result.upcomingObjectId,
          detail.contentId
        );
        break;
      case DetailType.UpcomingEpisodesDetail:
        result.sectionIds.add("upcoming");
        // Android reads this entry for BOTH ids — it is the only content-level
        // detail that carries a unified-collection id, so it doubles as the
        // Episode Guide seed when no seasonOrYears/onDemandEpisodes entry
        // supplied one.
        result.episodeGuideObjectId = firstDefined(
          result.episodeGuideObjectId,
          detail.unifiedCollectionId
        );
        result.upcomingObjectId = firstDefined(
          result.upcomingObjectId,
          detail.unifiedCollectionId
        );
        break;
      case DetailType.CastDetail:
        result.sectionIds.add("cast");
        break;
      case DetailType.MayAlsoLikeDetail:
        result.sectionIds.add("mayAlsoLike");
        result.mayAlsoLikeObjectId = firstDefined(
          result.mayAlsoLikeObjectId,
          detail.collectionId
        );
        break;
      default:
        break;
    }
  }

  return result;
}

/**
 * Single pass over a `TUnifiedCollectionScreenDetails` detail list — see
 * `DetailsScreenDataMapper.toUnified*ObjectId`.
 */
function scanUnifiedCollectionDetails(details: RawDetail[]): DetailScanResult {
  const result: DetailScanResult = {
    sectionIds: new Set<ContentDetailSectionId>(),
    episodeGuideObjectId: null,
    upcomingObjectId: null,
    mayAlsoLikeObjectId: null
  };

  for (const detail of details) {
    switch (detail.detailType) {
      case UnifiedCollectionDetailType.EpisodeGuideUnifiedCollectionDetail:
      case UnifiedCollectionDetailType.OnDemandEpisodesUnifiedCollectionDetail:
        result.sectionIds.add("episodes");
        result.episodeGuideObjectId = firstDefined(
          result.episodeGuideObjectId,
          detail.unifiedCollectionId
        );
        break;
      case UnifiedCollectionDetailType.UpcomingEpisodesUnifiedCollectionDetail:
        result.sectionIds.add("upcoming");
        result.episodeGuideObjectId = firstDefined(
          result.episodeGuideObjectId,
          detail.unifiedCollectionId
        );
        result.upcomingObjectId = firstDefined(
          result.upcomingObjectId,
          detail.unifiedCollectionId
        );
        break;
      case UnifiedCollectionDetailType.CastUnifiedCollectionDetail:
        result.sectionIds.add("cast");
        break;
      case UnifiedCollectionDetailType.MayAlsoLikeUnifiedCollectionDetail:
        result.sectionIds.add("mayAlsoLike");
        result.mayAlsoLikeObjectId = firstDefined(
          result.mayAlsoLikeObjectId,
          detail.collectionId
        );
        break;
      default:
        break;
    }
  }

  return result;
}

// ─── DTO extraction helpers ───

/**
 * Reads every `actions[]` list of the given detail type off a screen-details
 * response, in response order. A response can carry several — e.g. a
 * `WATCH_NOW_DETAIL` and a `GET_THIS_SHOW_DETAIL` `actionDetail` — and the
 * Watch and Schedule rows are split out of their union.
 */
function getActionsForDetailType(
  details: RawDetail[],
  actionDetailType: string
): Action[] {
  const actions: Action[] = [];
  for (const detail of details) {
    if (
      detail.detailType === actionDetailType &&
      detail.actions !== undefined
    ) {
      actions.push(...detail.actions);
    }
  }
  return actions;
}

/** Narrows a screen-details `detailList.details[]` to the structural shape. */
function asRawDetails(details: Array<{ detailType: string }>): RawDetail[] {
  return details as unknown as RawDetail[];
}

/**
 * Reads a top-level string field off the opaque `TPreview` payload.
 * `TPreview` is a flattened union with no strict type (see
 * PreviewFormatter.ts for the same defensive-read pattern), so this guards
 * against missing/non-string values rather than casting blindly.
 */
function readPreviewString(preview: TPreview, key: string): string | undefined {
  if (preview === null || preview === undefined) {
    return undefined;
  }
  const value: unknown = (preview as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Reads a top-level numeric field off the opaque `TPreview` payload.
 * Handles both native numbers and string-encoded numbers (e.g. "3.5")
 * since the preview API may serialize numeric fields as strings.
 * Returns `null` when the field is missing or not parseable as a finite number.
 */
function readPreviewNumber(preview: TPreview, key: string): number | null {
  if (preview === null || preview === undefined) {
    return null;
  }
  const value: unknown = (preview as Record<string, unknown>)[key];
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.length > 0) {
    const parsed: number = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Splits a comma-delimited preview string field into a trimmed array. */
function splitCommaList(value: string | undefined): string[] {
  if (value === undefined) {
    return [];
  }
  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Removes a leading role label from the preview credit string. */
export function sanitizeCreditString(
  creditString?: string
): string | undefined {
  if (!creditString) {
    return undefined;
  }
  return creditString.replace(/^(actor|cast|director):\s*/i, "").trim();
}

/** Joins mapped genres with the sheet's metadata separator. */
export function formatGenres(genres?: string[]): string | undefined {
  if (genres === undefined || genres.length === 0) {
    return undefined;
  }
  return genres.join(" · ");
}

/** Formats a season/episode title, using 1 as the missing-number fallback. */
export function formatEpisodeSubtitle(
  seasonNumber?: number,
  episodeNumber?: number,
  episodeTitle?: string
): string | undefined {
  if (seasonNumber === undefined && episodeNumber === undefined) {
    return undefined;
  }
  const prefix: string =
    "S" +
    String(seasonNumber !== undefined ? seasonNumber : 1) +
    ":E" +
    String(episodeNumber !== undefined ? episodeNumber : 1);
  return episodeTitle !== undefined && episodeTitle !== ""
    ? prefix + " - " + episodeTitle
    : prefix;
}

/**
 * Reads `preview.channel.callSign` for the network/channel name shown at
 * the end of the metadata row.
 */
function readPreviewNetworkName(preview: TPreview): string | undefined {
  if (preview === null || preview === undefined) {
    return undefined;
  }
  const channel = (preview as Record<string, unknown>).channel as
    | { callSign?: unknown }
    | undefined;
  if (channel === undefined || channel === null) {
    return undefined;
  }
  const callSign = channel.callSign;
  return typeof callSign === "string" && callSign.length > 0
    ? callSign
    : undefined;
}

// ─── Preview-derived presentation fields ───

/**
 * Formats a duration in seconds as the type-row runtime segment
 * ("1h 30m", "45m"). Returns `undefined` for a missing or non-positive
 * duration rather than rendering "0m".
 */
export function formatDuration(
  durationSeconds: number | null
): string | undefined {
  if (durationSeconds === null || durationSeconds <= 0) {
    return undefined;
  }
  const totalMinutes: number = Math.round(durationSeconds / SECONDS_PER_MINUTE);
  const hours: number = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes: number = totalMinutes % MINUTES_PER_HOUR;
  if (hours === 0) {
    return String(minutes) + "m";
  }
  if (minutes === 0) {
    return String(hours) + "h";
  }
  return String(hours) + "h " + String(minutes) + "m";
}

/**
 * Formats the airing line shown above the action rows, e.g.
 * "3/10 · 6:00PM - 8:00PM". Returns `undefined` when the preview carries no
 * start time.
 */
export function formatSchedule(preview: TPreview): string | undefined {
  const startTime: string | undefined = readPreviewString(preview, "startTime");
  if (startTime === undefined) {
    return undefined;
  }
  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) {
    return undefined;
  }
  const datePart: string =
    String(start.getMonth() + 1) + "/" + String(start.getDate());

  const endTime: string | undefined = readPreviewString(preview, "endTime");
  const startClock: string = formatClockTime(start);
  if (endTime === undefined) {
    return datePart + " · " + startClock;
  }
  const end = new Date(endTime);
  if (Number.isNaN(end.getTime())) {
    return datePart + " · " + startClock;
  }
  return datePart + " · " + startClock + " - " + formatClockTime(end);
}

/**
 * "6/1 8:00-9:30pm"-style airing range; the end is dropped when missing or
 * invalid. `undefined` when the start is missing or invalid.
 */
export function formatAiringRange(
  startTime: string | undefined,
  endTime: string | undefined
): string | undefined {
  if (startTime === undefined) {
    return undefined;
  }
  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) {
    return undefined;
  }
  let range: string =
    String(start.getMonth() + 1) +
    "/" +
    String(start.getDate()) +
    " " +
    formatClockTime(start);
  if (endTime !== undefined) {
    const end = new Date(endTime);
    if (!Number.isNaN(end.getTime())) {
      range += "-" + formatClockTime(end);
    }
  }
  return range;
}

const NOON_HOUR: number = 12;
const MINUTE_PAD_WIDTH: number = 2;

/** Formats a Date as a 12-hour clock time, e.g. "6:00pm". */
export function formatClockTime(value: Date): string {
  const rawHours: number = value.getHours();
  const suffix: string = rawHours >= NOON_HOUR ? "pm" : "am";
  const hours: number =
    rawHours % NOON_HOUR === 0 ? NOON_HOUR : rawHours % NOON_HOUR;
  const minutes: string = String(value.getMinutes()).padStart(
    MINUTE_PAD_WIDTH,
    "0"
  );
  return String(hours) + ":" + minutes + suffix;
}

/**
 * Builds the inline badge list for the metadata row — the maturity rating and
 * the video resolution, in the order Figma shows them ("PG HD").
 *
 * @param rating - Resolved maturity rating display name, when known
 * @param preview - The companion preview payload
 * @returns Zero, one or two badges
 */
export function buildBadges(
  rating: string | undefined,
  preview: TPreview
): ContentDetailBadgeData[] {
  const badges: ContentDetailBadgeData[] = [];
  if (rating !== undefined && rating.length > 0) {
    badges.push({
      id: "rating",
      label: rating,
      classStyle: "content-detail__badge--rating"
    });
  }
  const resolutionLabel: string | undefined = getVideoResolutionLabel(
    readPreviewString(preview, "videoResolution")
  );
  if (resolutionLabel !== undefined && resolutionLabel.length > 0) {
    badges.push({
      id: "resolution",
      label: resolutionLabel,
      classStyle: "content-detail__badge--resolution"
    });
  }
  return badges;
}

/**
 * Shared preview-derived fields, mapped identically for both seed shapes.
 */
function mapPreviewFields(preview: TPreview): Partial<ContentDetailState> {
  const movieYear: number | null = readPreviewNumber(preview, "movieYear");
  return {
    starRating: readPreviewNumber(preview, "starRating"),
    description: readPreviewString(preview, "description"),
    cast: splitCommaList(
      sanitizeCreditString(readPreviewString(preview, "creditString"))
    ),
    genres: splitCommaList(readPreviewString(preview, "categoryLabel")),
    releaseYear: movieYear !== null ? String(movieYear) : undefined,
    duration: formatDuration(readPreviewNumber(preview, "duration")),
    schedule: formatSchedule(preview),
    networkName: readPreviewNetworkName(preview)
  };
}

// ─── Public mappers ───

/**
 * True when a mapped payload carries no actionable pill at all. The web
 * client renders the screen regardless (with both action rows empty) rather
 * than treating this as a hard error, unlike Android's
 * `loadDetailsScreenFlow`; kept for callers that still want to detect the
 * condition.
 */
export function hasNoActions(data: Partial<ContentDetailState>): boolean {
  const watchCount: number =
    data.watchActions !== undefined ? data.watchActions.length : 0;
  const scheduleCount: number =
    data.scheduleActions !== undefined ? data.scheduleActions.length : 0;
  return watchCount === 0 && scheduleCount === 0;
}

/**
 * Maps a `ContentScreenDetails` response (contentId seed) plus its
 * companion `TPreview` payload into a partial `ContentDetailState`.
 *
 * @param dto - Response from ContentDetailInteractor.fetchContentScreenDetails
 * @param preview - Response from PreviewInteractor.fetchContentPreviewV3
 * @param kind - Caller-supplied content classification
 */
export function mapContentScreenDetailsToState(
  dto: ContentScreenDetails,
  preview: TPreview,
  kind: ContentDetailKind
): Partial<ContentDetailState> {
  const details: RawDetail[] = asRawDetails(dto.detailList.details);
  const actions: Action[] = getActionsForDetailType(
    details,
    DetailType.ActionDetail
  );
  const { watchActions, scheduleActions, ottProviders } =
    mapActionsToRows(actions);
  const scan: DetailScanResult = scanContentDetails(details);

  const dtoTitle: string | undefined = nonEmpty(dto.title);

  return {
    kind,
    title:
      dtoTitle !== undefined ? dtoTitle : readPreviewString(preview, "title"),
    contentType: getContentTypeLabel(kind),
    ...mapPreviewFields(preview),
    watchActions,
    scheduleActions,
    streamingServices: ottProviders,
    sections: buildSections(scan.sectionIds),
    episodeGuideObjectId: scan.episodeGuideObjectId,
    upcomingObjectId: scan.upcomingObjectId,
    mayAlsoLikeObjectId: scan.mayAlsoLikeObjectId
  };
}

/**
 * Maps a `TUnifiedCollectionScreenDetails` response (unifiedCollectionId
 * seed) plus its companion `TPreview` payload into a partial
 * `ContentDetailState`.
 *
 * `kind` is always mapped to `"series"` here: the team/league unified-
 * collection screen types share this screen's series UI (Episodes rail,
 * season picker), which is the correct behavior for them too.
 *
 * @param dto - Response from ContentDetailInteractor.fetchUnifiedCollectionScreenDetails
 * @param preview - Response from PreviewInteractor.fetchUnifiedCollectionPreview
 * @param playableEpisodeActions - Result of
 *   ContentDetailInteractor.resolvePlayableEpisodeActions — `null` until
 *   that resolver exists, in which case Watch Actions fall back to
 *   `actionUnifiedCollectionDetail.actions` from `dto`.
 */
export function mapUnifiedCollectionScreenDetailsToState(
  dto: TUnifiedCollectionScreenDetails,
  preview: TPreview,
  playableEpisodeActions: Action[] | null
): Partial<ContentDetailState> {
  const details: RawDetail[] = asRawDetails(dto.detailList.details);
  const screenActions: Action[] = getActionsForDetailType(
    details,
    UnifiedCollectionDetailType.ActionUnifiedCollectionDetail
  );
  const watchSourceActions: Action[] =
    playableEpisodeActions !== null && playableEpisodeActions.length > 0
      ? playableEpisodeActions
      : screenActions;

  // Watch Actions may come from the (not-yet-implemented) playable-episode
  // resolver; Schedule Actions (record series / manage recordings /
  // favorite) always come from the screen-details response — see
  // ContentDetailInteractor.resolvePlayableEpisodeActions.
  const { watchActions, ottProviders } = mapActionsToRows(watchSourceActions);
  const { scheduleActions } = mapActionsToRows(screenActions);

  const scan: DetailScanResult = scanUnifiedCollectionDetails(details);
  const dtoTitle: string | undefined = nonEmpty(dto.title);

  return {
    kind: "series",
    title:
      dtoTitle !== undefined ? dtoTitle : readPreviewString(preview, "title"),
    contentType: getContentTypeLabel("series"),
    ...mapPreviewFields(preview),
    watchActions,
    scheduleActions,
    streamingServices: ottProviders,
    sections: buildSections(scan.sectionIds),
    episodeGuideObjectId: scan.episodeGuideObjectId,
    upcomingObjectId: scan.upcomingObjectId,
    mayAlsoLikeObjectId: scan.mayAlsoLikeObjectId
  };
}

/**
 * Resolves the "Next Airing: …" caption for a title whose airing is in the
 * future — Figma frame `2198-6403`. Returns `undefined` when the preview has
 * no start time or the airing has already begun, in which case the plain
 * `schedule` line is shown instead.
 *
 * @param preview - The companion preview payload
 * @param networkName - Channel call sign appended after "on", when known
 * @param now - Reference instant (injected so tests are not clock-dependent)
 */
export function formatNextAiringText(
  preview: TPreview,
  networkName: string | undefined,
  now: Date
): string | undefined {
  const startTime: string | undefined = readPreviewString(preview, "startTime");
  if (startTime === undefined) {
    return undefined;
  }
  const start = new Date(startTime);
  if (Number.isNaN(start.getTime()) || start.getTime() <= now.getTime()) {
    return undefined;
  }

  const range: string | undefined = formatAiringRange(
    startTime,
    readPreviewString(preview, "endTime")
  );
  let airing: string = range !== undefined ? range : "";
  if (networkName !== undefined && networkName.length > 0) {
    airing += " on " + networkName;
  }

  return Locale.getString(LocaleKeys.CONTENT_NEXT_AIRING).replace(
    "{{airingDateTime}}",
    airing
  );
}
